import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster, toast } from "sonner";
import { Globe, CheckCircle, Hourglass, ClockCounterClockwise, XCircle, Copy, ArrowsClockwise, Trash } from "@phosphor-icons/react";

const STATUS_META = {
  none: { label: "Not configured", color: "bg-zinc-100 text-zinc-600 border-zinc-200", Icon: Globe },
  pending_approval: { label: "Pending super admin approval", color: "bg-amber-50 text-amber-800 border-amber-200", Icon: Hourglass },
  pending_dns: { label: "Awaiting DNS verification", color: "bg-blue-50 text-blue-800 border-blue-200", Icon: ClockCounterClockwise },
  active: { label: "Active", color: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle },
  rejected: { label: "Rejected", color: "bg-red-50 text-red-700 border-red-200", Icon: XCircle },
};

export default function DomainSettings() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [domain, setDomain] = useState("");
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/company/custom-domain");
      setState(data);
      setDomain(data.domain || "");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load domain status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const cleaned = domain.trim().toLowerCase();
    if (!cleaned) return toast.error("Enter a domain");
    setSaving(true);
    try {
      await api.post("/company/custom-domain", { domain: cleaned });
      toast.success("Domain request submitted — super admin will review.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not submit domain");
    } finally {
      setSaving(false);
    }
  };

  const verifyDns = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/company/custom-domain/verify-dns");
      if (data.ok) {
        toast.success("DNS verified — your custom domain is now Active.");
      } else {
        toast.error(data.message || "DNS not pointing to us yet — check your CNAME record.");
      }
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const removeDomain = async () => {
    if (!window.confirm("Remove your custom domain? Your customers will lose access via the branded URL.")) return;
    try {
      await api.delete("/company/custom-domain");
      toast.success("Custom domain removed.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Remove failed");
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  if (loading || !state) {
    return <div className="p-6 text-sm text-zinc-500">Loading…</div>;
  }

  const status = state.status || "none";
  const meta = STATUS_META[status] || STATUS_META.none;
  const hasRequest = status !== "none";
  const StatusIcon = meta.Icon;

  return (
    <div className="p-6 max-w-3xl" data-testid="domain-settings-page">
      <Toaster richColors position="top-right" />
      <div className="mb-6">
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1">
          Company settings
        </div>
        <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans" }}>
          Custom domain
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Run your portal under your own URL (e.g. <code>app.yourcompany.com</code>) instead of our platform host. Requires DNS access at your domain registrar.
        </p>
      </div>

      {!hasRequest ? (
        <form onSubmit={submit} className="border border-zinc-200 rounded-md p-6 bg-white" data-testid="domain-request-form">
          <Label htmlFor="domain-input" className="text-xs uppercase tracking-wider text-zinc-500">Your domain</Label>
          <Input
            id="domain-input"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="app.yourcompany.com"
            className="font-mono mt-1.5"
            data-testid="domain-input"
          />
          <p className="text-xs text-zinc-500 mt-2">
            Use a subdomain (like <code>app.</code> or <code>portal.</code>) — the apex/root domain isn&apos;t supported because CNAME records can&apos;t be applied there.
          </p>
          <Button type="submit" disabled={saving} className="mt-4 bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="domain-submit-btn">
            {saving ? "Submitting…" : "Request custom domain"}
          </Button>
        </form>
      ) : (
        <div className="space-y-5">
          <div className="border border-zinc-200 rounded-md p-5 bg-white">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Domain</div>
                <div className="font-mono text-lg font-semibold text-zinc-900 mt-0.5" data-testid="domain-current">{state.domain}</div>
              </div>
              <span
                className={`inline-flex items-center gap-1 text-[10px] uppercase font-semibold tracking-wider border rounded-full px-2.5 py-1 ${meta.color}`}
                data-testid="domain-status-badge"
              >
                <StatusIcon size={11} weight="fill" /> {meta.label}
              </span>
            </div>
            {status === "rejected" && state.rejected_reason && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-md p-3 mb-3">
                <b>Reason:</b> {state.rejected_reason}
              </div>
            )}
            {status === "active" && state.activated_at && (
              <div className="text-sm text-zinc-500">
                Activated {new Date(state.activated_at).toLocaleString()}. Your customers can sign up &amp; log in at{" "}
                <a href={`https://${state.domain}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                  https://{state.domain}
                </a>
              </div>
            )}
            <div className="flex gap-2 mt-2">
              <Button variant="outline" size="sm" onClick={removeDomain} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="domain-remove-btn">
                <Trash size={13} className="mr-1.5" /> Remove
              </Button>
              {status === "pending_dns" && (
                <Button onClick={verifyDns} disabled={verifying} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="domain-verify-btn">
                  <ArrowsClockwise size={13} className={`mr-1.5 ${verifying ? "animate-spin" : ""}`} />
                  {verifying ? "Checking…" : "Check DNS now"}
                </Button>
              )}
            </div>
          </div>

          {status === "pending_dns" && (
            <div className="border border-blue-200 bg-blue-50/50 rounded-md p-5" data-testid="dns-instructions">
              <h3 className="text-sm font-semibold text-blue-900 mb-3">DNS setup instructions</h3>
              <p className="text-xs text-blue-800 mb-4">
                Approval received! Now add <b>one</b> of these records at your DNS provider, then click <b>Check DNS now</b> above.
              </p>

              <div className="bg-white border border-blue-100 rounded p-3 mb-3">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-blue-700 mb-2">Option 1 — CNAME (recommended)</div>
                <div className="grid grid-cols-[80px_1fr_auto] gap-2 text-xs items-center">
                  <span className="text-zinc-500">Type</span>
                  <code className="font-mono">CNAME</code>
                  <span />
                  <span className="text-zinc-500">Host</span>
                  <code className="font-mono break-all">{state.domain}</code>
                  <button type="button" onClick={() => copy(state.domain)} className="text-zinc-400 hover:text-zinc-700"><Copy size={12} /></button>
                  <span className="text-zinc-500">Value</span>
                  <code className="font-mono break-all">{state.target_host}</code>
                  <button type="button" onClick={() => copy(state.target_host)} className="text-zinc-400 hover:text-zinc-700"><Copy size={12} /></button>
                </div>
              </div>

              <div className="bg-white border border-blue-100 rounded p-3">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-blue-700 mb-2">Option 2 — TXT (if CNAME isn&apos;t possible)</div>
                <div className="grid grid-cols-[80px_1fr_auto] gap-2 text-xs items-center">
                  <span className="text-zinc-500">Type</span>
                  <code className="font-mono">TXT</code>
                  <span />
                  <span className="text-zinc-500">Host</span>
                  <code className="font-mono break-all">_kanal-verify.{state.domain}</code>
                  <button type="button" onClick={() => copy(`_kanal-verify.${state.domain}`)} className="text-zinc-400 hover:text-zinc-700"><Copy size={12} /></button>
                  <span className="text-zinc-500">Value</span>
                  <code className="font-mono break-all">{state.verification_token}</code>
                  <button type="button" onClick={() => copy(state.verification_token)} className="text-zinc-400 hover:text-zinc-700"><Copy size={12} /></button>
                </div>
              </div>

              <p className="text-[11px] text-blue-700 mt-3">
                DNS changes can take a few minutes to propagate. We&apos;ll verify automatically when you click the button above.
              </p>
            </div>
          )}

          {status === "pending_approval" && (
            <div className="border border-amber-200 bg-amber-50/40 rounded-md p-5 text-sm text-amber-900">
              Your domain is awaiting platform-admin approval. Once approved, you&apos;ll see DNS instructions here.
            </div>
          )}
        </div>
      )}

      {/* Branded email sender — fires only when the courier wants their
          transactional emails to come from noreply@theirdomain.com instead of
          the platform default. Independent of the custom-domain flow above. */}
      <EmailSenderSection />
    </div>
  );
}


function EmailSenderSection() {
  const [state, setState] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [domain, setDomain] = React.useState("");
  const [localPart, setLocalPart] = React.useState("kanal");
  const [replyTo, setReplyTo] = React.useState("");
  const [replySaving, setReplySaving] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [verifying, setVerifying] = React.useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/company/email-sender");
      setState(data);
      setDomain(data.domain || "");
      setLocalPart(data.local_part || "kanal");
      setReplyTo(data.reply_to || "");
    } catch (e) {
      // Endpoint requires company_admin/super_admin; silently skip if forbidden.
      setState(null);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { load(); }, []);

  const register = async () => {
    const d = domain.trim().toLowerCase();
    if (!d) return toast.error("Enter a sending domain");
    setSubmitting(true);
    try {
      await api.post("/company/email-sender", { domain: d, local_part: localPart || "kanal" });
      toast.success("Domain registered with Resend. Add the DNS records below, then verify.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  const saveReplyTo = async () => {
    const addr = replyTo.trim().toLowerCase();
    setReplySaving(true);
    try {
      await api.put("/company/email-reply-to", { reply_to: addr });
      toast.success(addr ? "Reply-to saved" : "Reply-to cleared");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to save");
    } finally {
      setReplySaving(false);
    }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/company/email-sender/verify");
      if (data.ok) toast.success("Verified — your branded sender is now Active.");
      else toast.error("Not verified yet — give DNS a few more minutes to propagate.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Verify failed");
    } finally {
      setVerifying(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove your branded email sender? Future emails will go out from the platform default.")) return;
    try {
      await api.delete("/company/email-sender");
      toast.success("Removed.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Remove failed");
    }
  };

  if (loading) return null;

  const status = state?.status || "none";
  const hasSender = status !== "none";
  const isActive = status === "active";

  return (
    <div className="mt-8 border-t border-zinc-100 pt-8" data-testid="email-sender-section">
      <div className="mb-4">
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1">
          Customer emails
        </div>
        <h2 className="text-xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans" }}>
          Branded email sender
        </h2>
        <p className="text-sm text-zinc-500 mt-1">
          Have invoice receipts, tracking notifications, and verification emails come from <code>kanal@yourcompany.com</code> instead of the platform&apos;s default sender.
        </p>
      </div>

      {/* Reply-To (independent from the branded-sender domain — works even
          without any DNS setup so customer replies land in the courier's
          own inbox). */}
      <div className="border border-zinc-200 rounded-md p-5 bg-white mb-5" data-testid="email-reply-to-form">
        <Label htmlFor="email-reply-to" className="text-xs uppercase tracking-wider text-zinc-500">Reply-To email</Label>
        <p className="text-xs text-zinc-500 mt-1 mb-2">
          When customers hit &quot;Reply&quot; on any email, their message will be delivered to this address.
        </p>
        <div className="flex flex-col sm:flex-row gap-2 mt-1.5">
          <Input
            id="email-reply-to"
            type="email"
            value={replyTo}
            onChange={(e) => setReplyTo(e.target.value)}
            placeholder="support@yourcompany.com"
            className="font-mono flex-1"
            data-testid="email-reply-to-input"
          />
          <Button
            onClick={saveReplyTo}
            disabled={replySaving}
            className="bg-zinc-900 hover:bg-zinc-800 text-white"
            data-testid="email-reply-to-save-btn"
          >
            {replySaving ? "Saving…" : "Save"}
          </Button>
        </div>
        {state?.reply_to && (
          <div className="text-xs text-zinc-500 mt-2" data-testid="email-reply-to-current">
            Current reply-to: <code className="font-mono text-zinc-800">{state.reply_to}</code>
          </div>
        )}
      </div>

      {!hasSender ? (
        <div className="border border-zinc-200 rounded-md p-5 bg-white" data-testid="email-sender-form">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-3 mb-3">
            <div>
              <Label htmlFor="email-local" className="text-xs uppercase tracking-wider text-zinc-500">Local part</Label>
              <Input
                id="email-local"
                value={localPart}
                onChange={(e) => setLocalPart(e.target.value.toLowerCase())}
                placeholder="kanal"
                className="font-mono mt-1.5"
                data-testid="email-sender-local-input"
              />
            </div>
            <div>
              <Label htmlFor="email-domain" className="text-xs uppercase tracking-wider text-zinc-500">Domain</Label>
              <Input
                id="email-domain"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="yourcompany.com"
                className="font-mono mt-1.5"
                data-testid="email-sender-domain-input"
              />
            </div>
          </div>
          <p className="text-xs text-zinc-500 mb-3">
            Preview: <code className="font-mono">{(localPart || "kanal")}@{domain || "yourcompany.com"}</code>
          </p>
          <Button onClick={register} disabled={submitting} className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="email-sender-register-btn">
            {submitting ? "Registering…" : "Register sending domain"}
          </Button>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="border border-zinc-200 rounded-md p-5 bg-white">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Sending as</div>
                <div className="font-mono text-lg font-semibold text-zinc-900 mt-0.5" data-testid="email-sender-current">{state.preview_from || `${state.local_part}@${state.domain}`}</div>
              </div>
              <span
                className={`inline-flex items-center gap-1 text-[10px] uppercase font-semibold tracking-wider border rounded-full px-2.5 py-1 ${isActive ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}
                data-testid="email-sender-status-badge"
              >
                {isActive ? <CheckCircle size={11} weight="fill" /> : <Hourglass size={11} weight="fill" />}
                {isActive ? "Active" : status === "pending" ? "Awaiting DNS" : status}
              </span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={remove} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="email-sender-remove-btn">
                <Trash size={13} className="mr-1.5" /> Remove
              </Button>
              {!isActive && (
                <Button onClick={verify} disabled={verifying} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="email-sender-verify-btn">
                  <ArrowsClockwise size={13} className={`mr-1.5 ${verifying ? "animate-spin" : ""}`} />
                  {verifying ? "Checking…" : "Check DNS now"}
                </Button>
              )}
            </div>
          </div>

          {!isActive && (state.records || []).length > 0 && (
            <div className="border border-blue-200 bg-blue-50/50 rounded-md p-5" data-testid="email-sender-records">
              <h3 className="text-sm font-semibold text-blue-900 mb-3">DNS records to add</h3>
              <p className="text-xs text-blue-800 mb-4">
                Add all of these at your DNS provider (one row per record). DNS can take up to 30 minutes to propagate.
              </p>
              <div className="bg-white border border-blue-100 rounded p-3 text-xs overflow-auto">
                <table className="w-full">
                  <thead className="text-zinc-500 text-[10px] uppercase tracking-wider">
                    <tr>
                      <th className="text-left pb-1.5">Type</th>
                      <th className="text-left pb-1.5">Host</th>
                      <th className="text-left pb-1.5">Value</th>
                      <th className="text-left pb-1.5">TTL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.records.map((r, i) => (
                      <tr key={i} className="border-t border-blue-50">
                        <td className="py-1.5 font-mono">{r.record || r.type}</td>
                        <td className="py-1.5 font-mono break-all">{r.name}</td>
                        <td className="py-1.5 font-mono break-all">{r.value}</td>
                        <td className="py-1.5 text-zinc-500">{r.ttl || "Auto"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
